import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  getPurchaseOrderBranch,
  getPurchaseOrderBranchById,
  getParticularPage,
  getSaleOrderDetail,
  getNext,
  getPrevious,
  handleSearch,
  getPurchaseOrderBranchAdditionalChargeList,
  getPurchaseOrderBranchChargeStatus,
  createPurchaseOrderBranch,
} from "./thunk";
import { NepaliDate } from "@zener/nepali-datepicker-react";
import { dateFormater } from "../../../../../../Utils/dateFormater";

const initialState = {
  purchaseOrderBranchs: [],
  edit: false,
  purchaseOrderBranch: null,
  count: 0,
  pageNo: 1,
  next: null,
  previous: null,
  loading: false,
  loadingCreate: false,
  loadingUpdated: false,
  loadingPurchaseOrderBranch: false,
  loadingNext: false,
  loadingDetails: false,
  saveAndContinue: false,
  purchaseOrderBranchDetails: [
    {
      product: null,
      vendor: null,
      closingStock: "",
      description: "",
      actualQuantity: "",
      unit: "",
      altQuantity: "",
      altUnit: null,
      rate: "",
      amount: "",
      requiredDate: dateFormater(new NepaliDate()),
      unique: uuidv4(),
    },
  ],
  additionalCharges: null,
  hasAdditionalCharges: false,
  showCreateModal: false,
};

export const purchaseOrderBranchSlice = createSlice({
  name: "purchaseOrderBranch",
  initialState,
  reducers: {
    setShowCreateModal: (state, action) => {
      state.showCreateModal = action.payload;
    },
    setSaveAndContinue: (state, { payload }) => {
      state.saveAndContinue = payload;
    },
    goToPage: (state, { payload }) => {
      state.pageNo = payload;
    },
    updatedAdditionalCharges: (state, action) => {
      state.additionalCharges = action.payload;
    },
    purchaseOrderBranchDetailsUpdated: (state, action) => {
      const sam = action.payload?.reduce((acc, pos) => {
        return Number(pos.actualQuantity) + acc;
      }, 0);
      state.purchaseOrderBranchDetails = action.payload;
      state.additionalCharges = state?.additionalCharges?.map((value) => {
        return { ...value, amount: value.canEdit ? value.rate * sam : value.amount };
      });
    },

    clearPurchaseOrderBranchDetails: (state) => {
      state.purchaseOrderBranchDetails = [
        {
          product: null,
          vendor: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate()),
          unique: uuidv4(),
        },
      ];
    },
    clearAllPurchaseOrderBranch: (state) => {
      state.edit = false;
      state.loading = false;
      state.loadingUpdated = false;
      state.loadingPurchaseOrderBranch = false;
      state.purchaseOrderBranch = null;
      state.saveAndContinue = false;
      state.purchaseOrderBranchDetails = [
        {
          product: null,
          vendor: null,
          closingStock: "",
          description: "",
          actualQuantity: "",
          unit: "",
          altQuantity: "",
          altUnit: null,
          rate: "",
          amount: "",
          requiredDate: dateFormater(new NepaliDate()),
          unique: uuidv4(),
        },
      ];
      state.hasAdditionalCharges = false;
      state.additionalCharges = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createPurchaseOrderBranch.pending, (state) => {
      state.loadingCreate = true;
    });
    builder.addCase(createPurchaseOrderBranch.fulfilled, (state, action) => {
      state.loadingCreate = false;
    });
    builder.addCase(createPurchaseOrderBranch.rejected, (state) => {
      state.loadingCreate = false;
    });
    builder.addCase(getPurchaseOrderBranchChargeStatus.pending, (state) => {
      state.hasAdditionalCharges = false;
    });
    builder.addCase(getPurchaseOrderBranchChargeStatus.fulfilled, (state, action) => {
      state.hasAdditionalCharges = action.payload.data.length > 0 ? true : false;
    });
    builder.addCase(getPurchaseOrderBranchChargeStatus.rejected, (state) => {
      state.hasAdditionalCharges = null;
    });
    builder.addCase(getPurchaseOrderBranchAdditionalChargeList.pending, (state) => {
      state.additionalCharges = null;
    });
    builder.addCase(getPurchaseOrderBranchAdditionalChargeList.fulfilled, (state, action) => {
      state.additionalCharges = action.payload.data;
    });
    builder.addCase(getPurchaseOrderBranchAdditionalChargeList.rejected, (state) => {
      state.additionalCharges = null;
    });
    // get
    builder.addCase(getSaleOrderDetail.pending, (state) => {
      state.loadingDetails = true;
      state.purchaseOrderBranchDetails = null;
    });
    builder.addCase(getSaleOrderDetail.fulfilled, (state, action) => {
      state.loadingDetails = false;
      state.purchaseOrderBranchDetails = action.payload.data;
    });
    builder.addCase(getSaleOrderDetail.rejected, (state) => {
      state.loadingDetails = false;
      state.purchaseOrderBranchDetails = null;
    });
    // getPurchaseOrderBranchById
    builder.addCase(getPurchaseOrderBranchById.pending, (state) => {
      state.loading = true;
      state.purchaseOrderBranch = null;
    });
    builder.addCase(getPurchaseOrderBranchById.fulfilled, (state, action) => {
      state.edit = true;
      state.loading = false;
      state.purchaseOrderBranch = action.payload.data;
      state.purchaseOrderBranchDetails = action.payload.data?.purchaseOrderDetails.map((value) => {
        return { ...value, unique: uuidv4() };
      });
      state.additionalCharges = action.payload.data?.purchaseOrderBranchMainAdditionalChargeCost;
    });
    builder.addCase(getPurchaseOrderBranchById.rejected, (state) => {
      state.edit = false;
      state.loading = false;
      state.purchaseOrderBranch = null;
    });
    builder.addMatcher(
      isAnyOf(
        getPurchaseOrderBranch.pending,
        getParticularPage.pending,
        getPrevious.pending,
        getNext.pending,
        handleSearch.pending
      ),
      (state) => {
        state.loadingPurchaseOrderBranch = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getPurchaseOrderBranch.fulfilled,
        getParticularPage.fulfilled,
        getPrevious.fulfilled,
        getNext.fulfilled,
        handleSearch.fulfilled
      ),
      (state, action) => {
        state.loadingPurchaseOrderBranch = false;
        state.purchaseOrderBranchs = action.payload.data;
        state.count = action.payload.totalCount;

        state.previous = action.payload.previous;
        state.next = action.payload.next;
      }
    );
    builder.addMatcher(
      isAnyOf(
        getPurchaseOrderBranch.rejected,
        getParticularPage.rejected,
        getPrevious.rejected,
        getNext.rejected,
        handleSearch.rejected
      ),
      (state) => {
        state.loadingPurchaseOrderBranch = false;
      }
    );
  },
});

export const {
  setShowCreateModal,
  setSaveAndContinue,
  goToPage,
  updatedAdditionalCharges,
  purchaseOrderBranchDetailsUpdated,
  clearPurchaseOrderBranchDetails,
  clearAllPurchaseOrderBranch,
} = purchaseOrderBranchSlice.actions;

export default purchaseOrderBranchSlice.reducer;
